// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="SegmentedControl" set="avila-roffe-shadcn-studio" verified=tbd

import type { LucideIcon } from 'lucide-react';
import type * as React from 'react';
import { cn } from '../../lib/utils';
import { ToggleGroup, ToggleGroupItem } from './toggle-group';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SegmentedControlOption {
  value: string;
  label: React.ReactNode;
  icon?: LucideIcon;
}

export interface SegmentedControlProps {
  options: ReadonlyArray<SegmentedControlOption>;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic segmented toggle control — a pill-track with N mutually-exclusive options.
 * Outer radius follows `--field-radius` (same as inputs, selects, and textareas).
 *
 * @example
 * <SegmentedControl
 *   options={[
 *     { value: '', label: 'Default' },
 *     { value: 'stacked', label: 'Stacked' },
 *     { value: 'side-label', label: 'Side label' },
 *   ]}
 *   value={value}
 *   onValueChange={onChange}
 * />
 *
 * @example
 * // With icons
 * <SegmentedControl
 *   options={[
 *     { value: 'grid', label: 'Grid', icon: LayoutGrid },
 *     { value: 'list', label: 'List', icon: List },
 *   ]}
 *   value={layout}
 *   onValueChange={setLayout}
 * />
 */
export function SegmentedControl({
  options,
  value,
  onValueChange,
  className,
}: SegmentedControlProps) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => {
        // Allow deselect only if value is already deselected — preserve current on re-click
        if (v !== undefined) onValueChange?.(v);
      }}
      style={
        {
          '--sc-inner-radius': 'calc(max(0px, var(--field-radius, var(--radius)) - 2px))',
        } as React.CSSProperties
      }
      className={cn(
        'inline-flex w-full gap-0 rounded-[var(--field-radius,var(--radius))] border border-border bg-muted/30 p-1',
        className,
      )}
    >
      {options.map(({ value: val, label, icon: Icon }) => (
        <ToggleGroupItem
          key={val}
          value={val}
          className="flex h-8 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-[var(--sc-inner-radius)] text-xs font-medium text-muted-foreground transition-all hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
        >
          {Icon && <Icon className="size-3.5 shrink-0" aria-hidden="true" />}
          {label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

SegmentedControl.displayName = 'SegmentedControl';
